/**
 * 对手范围模型。见 docs/ARCHITECTURE.md §10.5。
 *
 * 默认 `{ kind: 'random' }`：口径可解释（UI 标「vs N 个随机手牌」）、无需维护
 * 范围表正确性、最快。它的偏差是真实的——真实对手（尤其加注过的）范围更紧，
 * 所以 random 会**系统性高估**边缘牌的胜率。因此 §10.5 强制两件事：
 * UI 永远显示口径标签；提供一键切到 `{ kind:'top', percent:20 }`。这两件事
 * 在表现层做，本模块只负责把模型展开成可采样 / 可枚举的组合向量。
 *
 * 红线 #10 备注：这里没有任何「AI 用什么范围、什么时候切范围」的策略参数，
 * 只有「给定范围如何展开」的纯计算。策略参数一律留在 packages/server。
 */
import type { CardId } from '../ids.js'
import { NUM_CARDS, SHORT_DECK_MISSING } from '../poker/cards.js'
import { type DeckVariant, InvariantError } from '../poker/types.js'
import {
  HAND_CLASSES,
  HAND_CLASS_COMBOS,
  TOTAL_COMBOS_BY_VARIANT,
  classIndexOfCards,
  isClassInVariant,
} from './classes.js'
import { preflopEquityByClassIndex } from './preflop.js'

export type OpponentModel =
  | { kind: 'random' }
  | { kind: 'top'; percent: number }
  | { kind: 'weighted'; combos: Uint16Array; weights: Float32Array }

/**
 * 组合编码：`lowCard * 52 + highCard`（low < high），取值 0..2701，塞得进 Uint16。
 * 用单个整数而不是两张牌的数组，是为了让范围向量能放进 TypedArray——
 * 蒙特卡洛内层每次采样都要读它，对象数组会把 GC 压力推到天上。
 */
export function encodeCombo(a: CardId, b: CardId): number {
  return a < b ? a * NUM_CARDS + b : b * NUM_CARDS + a
}
export function comboLow(code: number): CardId {
  return (code / NUM_CARDS) | 0
}
export function comboHigh(code: number): CardId {
  return code % NUM_CARDS
}

/**
 * 展开后的范围。`cum` 是累计权重，采样时二分即可；`uniform` 标记
 * 「全部可用组合等权」，此时蒙特卡洛可以走「直接从牌堆均匀抽两张」的快路径
 * ——数学上与按组合采样完全等价，但省掉二分与碰撞重采样。
 */
export interface RangeVector {
  readonly combos: Uint16Array
  readonly weights: Float64Array
  readonly cum: Float64Array
  readonly total: number
  readonly uniform: boolean
}

function deadMaskOf(dead: readonly CardId[], variant: DeckVariant): Uint8Array {
  const mask = new Uint8Array(NUM_CARDS)
  for (let i = 0; i < dead.length; i++) {
    const c = dead[i]!
    if (c >= 0 && c < NUM_CARDS) mask[c] = 1
  }
  // 短牌里 2-5 根本发不出来：即使调用方没把它们放进 dead，也不能进范围
  if (variant === 'shortDeck') for (const c of SHORT_DECK_MISSING) mask[c] = 1
  return mask
}

function finish(combos: number[], weights: number[], allAvailable: number): RangeVector {
  if (combos.length === 0) {
    // 范围被 dead 牌掏空（例如 top 1% 全是 AA 而两张 A 在公共牌上）。
    // 继续算下去会得到 0/0，必须在这里报错而不是返回 NaN 胜率。
    throw new InvariantError('buildRangeCombos: 范围在排除已知牌后为空')
  }
  const cum = new Float64Array(combos.length)
  let acc = 0
  let uniform = combos.length === allAvailable
  const first = weights[0]!
  for (let i = 0; i < combos.length; i++) {
    const w = weights[i]!
    if (w !== first) uniform = false
    acc += w
    cum[i] = acc
  }
  return {
    combos: Uint16Array.from(combos),
    weights: Float64Array.from(weights),
    cum,
    total: acc,
    uniform,
  }
}

/**
 * 把模型展开成组合向量，排除所有已知牌（自己的底牌、公共牌、publicDead）。
 *
 * 注意 `dead` 的语义（§10.1 第 2 条）：只有**对全场公开亮出**的牌能进来。
 * 面朝下 muck 的牌进了这里，就成了一条把弃牌信息喂回给在局玩家的作弊通道。
 *
 * `variant`：短牌下 top 模型按短牌口径切（见 `topPercentClassWeights`），2-5 一律排除。
 */
export function buildRangeCombos(
  model: OpponentModel,
  dead: readonly CardId[],
  variant: DeckVariant = 'standard',
): RangeVector {
  const blocked = deadMaskOf(dead, variant)
  let available = 0
  for (let a = 0; a < NUM_CARDS; a++) {
    if (blocked[a] === 1) continue
    for (let b = a + 1; b < NUM_CARDS; b++) if (blocked[b] !== 1) available++
  }

  const combos: number[] = []
  const weights: number[] = []

  if (model.kind === 'random') {
    for (let a = 0; a < NUM_CARDS; a++) {
      if (blocked[a] === 1) continue
      for (let b = a + 1; b < NUM_CARDS; b++) {
        if (blocked[b] === 1) continue
        combos.push(a * NUM_CARDS + b)
        weights.push(1)
      }
    }
    return finish(combos, weights, available)
  }

  if (model.kind === 'top') {
    const classWeights = topPercentClassWeights(model.percent, variant)
    for (let a = 0; a < NUM_CARDS; a++) {
      if (blocked[a] === 1) continue
      for (let b = a + 1; b < NUM_CARDS; b++) {
        if (blocked[b] === 1) continue
        const w = classWeights[classIndexOfCards(a, b)]!
        if (w <= 0) continue
        combos.push(a * NUM_CARDS + b)
        weights.push(w)
      }
    }
    return finish(combos, weights, available)
  }

  if (model.combos.length !== model.weights.length) {
    throw new InvariantError(
      `buildRangeCombos: weighted 模型的 combos(${model.combos.length}) 与 weights(${model.weights.length}) 长度不等`,
    )
  }
  const seen = new Set<number>()
  for (let i = 0; i < model.combos.length; i++) {
    const code = model.combos[i]!
    const w = model.weights[i]!
    if (!(w > 0)) continue
    const lo = comboLow(code)
    const hi = comboHigh(code)
    if (lo >= hi || hi >= NUM_CARDS) {
      throw new InvariantError(`buildRangeCombos: 非法组合编码 ${code}`)
    }
    if (blocked[lo] === 1 || blocked[hi] === 1) continue // 与已知牌冲突，直接剔除
    if (seen.has(code)) continue
    seen.add(code)
    combos.push(code)
    weights.push(w)
  }
  return finish(combos, weights, available)
}

/**
 * top N% 的类权重（169 项，0..1）。
 *
 * 百分比按**组合数**算而不是按「169 个类」算：AA 只有 6 个组合，AKo 有 12 个，
 * 按类数切会让紧范围被系统性高估。边界上的那个类拿小数权重，
 * 这样 percent 是连续的，UI 拖滑杆不会一格跳掉 1.5% 的组合。
 *
 * ★ 短牌：百分比的分母是 630 个组合，只在 81 个发得出来的类里累计。
 *   直接用标准口径切会让范围宽一倍（「前 18%」里一半的类在短牌里不存在，
 *   剔除后剩下的类实际占短牌组合的 35% 左右），AI 会系统性高估自己的胜率。
 *   第一版的强度顺序取标准顺序的子序列（还没有短牌翻前胜率表）。
 */
export function topPercentClassWeights(percent: number, variant: DeckVariant = 'standard'): Float64Array {
  if (!(percent > 0) || percent > 100) {
    throw new InvariantError(`topPercentClassWeights: percent 必须在 (0,100]，收到 ${percent}`)
  }
  const target = (percent / 100) * TOTAL_COMBOS_BY_VARIANT[variant]
  const order = RANGE_ORDER_INDICES_BY_VARIANT[variant]
  const out = new Float64Array(169)
  let acc = 0
  for (let i = 0; i < order.length; i++) {
    const ci = order[i]!
    const size = HAND_CLASS_COMBOS[ci]!
    if (acc + size <= target) {
      out[ci] = 1
      acc += size
    } else {
      const frac = (target - acc) / size
      if (frac > 0) out[ci] = frac
      break
    }
  }
  return out
}

/**
 * 169 手牌按「对 1 个随机手牌的翻前 equity」降序的类索引。
 *
 * §10.5 让离线脚本 `gen-range-order.ts` 跑 200 万次 MC 生成并签入；这里直接由
 * 已签入的 `preflop-vs-random.json` 的 N=1 列派生（那一列本身就是分层抽样的
 * 49 万次发牌，精度更高）。少一个产物，也就不会出现「两个产物排序不一致」。
 * 排序对 equity 相等的类按规范索引破平，保证跨平台逐位确定。
 */
const RANGE_ORDER_INDICES: readonly number[] = Object.freeze(
  Array.from({ length: 169 }, (_, i) => i).sort((a, b) => {
    const ea = preflopEquityByClassIndex(a, 1)
    const eb = preflopEquityByClassIndex(b, 1)
    return eb === ea ? a - b : eb - ea
  }),
)

/**
 * 短牌的 81 个类，按强度降序。第一版取标准顺序的子序列：
 * 等有了短牌翻前胜率表，再换成按短牌胜率排序（接口不变）。
 */
const RANGE_ORDER_SHORT_INDICES: readonly number[] = Object.freeze(
  RANGE_ORDER_INDICES.filter((ci) => isClassInVariant(ci, 'shortDeck')),
)

const RANGE_ORDER_INDICES_BY_VARIANT: Readonly<Record<DeckVariant, readonly number[]>> = Object.freeze({
  standard: RANGE_ORDER_INDICES,
  shortDeck: RANGE_ORDER_SHORT_INDICES,
})

/** 169 个类名，按对随机手牌的翻前强度降序（RANGE_ORDER[0] === 'AA'）。 */
export const RANGE_ORDER: readonly string[] = Object.freeze(
  RANGE_ORDER_INDICES.map((i) => HAND_CLASSES[i]!),
)

/** 短牌的 81 个类名，按强度降序（RANGE_ORDER_SHORT[0] === 'AA'）。 */
export const RANGE_ORDER_SHORT: readonly string[] = Object.freeze(
  RANGE_ORDER_SHORT_INDICES.map((i) => HAND_CLASSES[i]!),
)

/** 类索引 → 在 RANGE_ORDER 中的名次（0 最强）。 */
export const RANGE_RANK_BY_CLASS: readonly number[] = Object.freeze(
  (() => {
    const rank = new Array<number>(169).fill(0)
    RANGE_ORDER_INDICES.forEach((ci, pos) => {
      rank[ci] = pos
    })
    return rank
  })(),
)
